<template>
  <div class="section">
    <div class="container">
      <div class="columns is-vcentered">
        <div class="column is-one-third">
          <breadcrumbs :history="[{ label: 'Payments', name: 'payments'}]" current="List" :hideBack="true" />
        </div>
        <div class="column is-one-third">
          <h1 class="has-text-centered is-size-4">Payments</h1>
        </div>
      </div>
      <!-- Filters Toolbar -->
      <div class="has-background-white rounded-lg pa-md mb-xxs">
        <div class="columns">
          <div class="column">
            <PaymentFilters :applied-filters="filters" @filter="filter" />
          </div>
          <div class="column is-narrow">
            <b-select v-model="orderBy" size="is-medium" expanded @input="sort">
              <option value="-id">Most Recent</option>
              <option value="id">Oldest</option>
            </b-select>
          </div>
          <div v-if="context.isMerchant()" class="column is-narrow">
            <b-button
              size="is-medium"
              outlined
              expanded
              @click.prevent="showCreatePaymentModal = true">
              Create Payment
            </b-button>
          </div>
        </div>
      </div>
      <!-- Actions Toolbar -->
      <div class="has-background-white rounded-lg px-md py-sm mb-xxs">
        <div class="columns is-vcentered is-mobile">
          <div class="column is-narrow">
            <b-dropdown :disabled="loading || selected.length === 0">
              <button class="button" slot="trigger" slot-scope="{ active }" :disabled="selected.length === 0">
                <span>Actions</span>
                <b-icon :icon="active ? 'menu-up' : 'menu-down'"></b-icon>
              </button>
              <template v-if="context.isSupplier()">
                <b-dropdown-item has-link><a href="#" @click.prevent class="has-text-grey-light">No Actions</a></b-dropdown-item>
              </template>
              <template v-if="context.isMerchant()">
                <b-dropdown-item has-link><a href="#" @click.prevent class="has-text-grey-light">No Actions</a></b-dropdown-item>
              </template>
            </b-dropdown>
          </div>
          <div class="column">
            <div class="has-text-right is-size-7">
              <!-- Counts -->
              <p v-if="loading">Loading...</p>
              <p v-else>{{ total }} {{ (total === 1) ? 'payment' : 'payments' }} found.</p>
            </div>
          </div>
        </div>
      </div>
      <!-- Results -->
      <div>
        <div class="mb-md">
          <b-table
            :data="results"
            :checked-rows.sync="selected"
            :mobile-cards="false"
            :loading="loading"
            hoverable
            checkable
            checkbox-position="left"
            class="is-size-7">
            <template>
              <b-table-column
                label="ID"
                cell-class="nowrap"
                width="64"
                v-slot="props">
                <router-link class="has-text-info has-text-weight-bold" :to="{ name: 'payment.detail', params: { id: props.row.id } }">
                {{ props.row.id }}
                </router-link>
              </b-table-column>
              <b-table-column
                v-if="context.isMerchant()"
                label="Supplier Name"
                header-class="nowrap"
                cell-class="nowrap"
                v-slot="props">
                <p>{{ props.row.brand.name }}</p>
              </b-table-column>
              <b-table-column
                v-if="context.isSupplier()"
                label="Retailer Name"
                header-class="nowrap"
                cell-class="nowrap"
                width="128"
                v-slot="props">
                <p>{{ props.row.retailer.name }}</p>
              </b-table-column>
              <b-table-column
                label="No.Of Invoices"
                header-class="nowrap"
                cell-class="nowrap"
                width="128"
                v-slot="props">
                <p>{{ props.row.num_invoices }}</p>
              </b-table-column>
              <b-table-column
                label="Paid Amount"
                header-class="nowrap"
                cell-class="nowrap"
                width="128"
                v-slot="props">
                <p>{{ $numbers(props.row.check_amount).formatCurrency({ mantissa: 2 }) }}</p>
              </b-table-column>
              <b-table-column
                v-if="context.isMerchant()"
                label="Payment Issue Date"
                header-class="nowrap"
                cell-class="nowrap"
                width="128"
                v-slot="props">
                <p v-if="props.row.check_issue_date !== null">{{ $dates.utc(props.row.check_issue_date).local().format('MMM D, YYYY') }}</p>
                <p v-else> - </p>
              </b-table-column>
              <b-table-column
                v-if="context.isSupplier()"
                label="Payment Record Date"
                header-class="nowrap"
                cell-class="nowrap"
                width="128"
                v-slot="props">
                <p>{{ $dates.utc(props.row.recorded_at).local().format('MMM D, YYYY') }}</p>
              </b-table-column>
              <b-table-column
                label="Status"
                header-class="nowrap"
                cell-class="nowrap"
                width="100"
                v-slot="props">
                <p>{{ $utils.upperFirst(props.row.status) }}</p>
              </b-table-column>
            </template>
            <template #empty>
              <div class="py-xxl has-text-grey has-text-centered">
                {{ (loading) ? "Loading data..." : 'No payments found!' }}
              </div>
            </template>
          </b-table>
        </div>
        <b-pagination
          :total="total"
          :current.sync="currentPage"
          :rounded="true"
          :per-page="perPage"
          @change="paginate">
        </b-pagination>
      </div>
      <!-- Modal: Create -->
      <b-modal
        has-modal-card
        scroll="keep"
        :active.sync="showCreatePaymentModal"
        :can-cancel="['x']"
        ><CreateModal />
      </b-modal>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Filterable from '@/mixins/Filterable'
import CreateModal from '@/components/Models/Payment/Modals/CreateModal'
import PaymentFilters from '../components/List/PaymentFilters'
import presets from '../presets'

export default {
  components: {
    PaymentFilters,
    CreateModal
  },
  data () {
    return {
      key: 'payments',
      presets: presets,
      fields: {
        short: 1
      },
      showCreatePaymentModal: false
    }
  },
  computed: {
    ...mapGetters(['context']),
    endpoint () {
      return this.context.route + '/payments/'
    },
    alwaysAttach () {
      let query = this.fields
      if (this.context.isSupplier()) {
        query = {
          ...this.fields,
          status: 'paid'
        }
      }
      return query
    }
  },
  methods: {
    reload () {
      this.selected = []
      this.load()
    }
  },
  mounted () {
    this.load()
  },
  mixins: [Filterable]
}
</script>
